import type { Asserts } from 'yup';
import * as yup from 'yup';
import type {
  PrintBaseClientData,
  PrintBaseOptions,
} from '@/components/modals/Print/types';
import { KASKOTYP } from '@/features/Sparten/shared/types';

const keyValueSchema = yup.object({
  key: yup.number(),
  value: yup.string(),
});

export const vuInformationSchema = yup.object({
  schnittstelleID: yup.number().required(),
  gesellschaftIDTrixi: yup.number().required(),
  gesellschaftIDGDV: yup.number().required(),
  gesellschaftIDGDVLang: yup.number().required(),
  gesellschaftBezeichnung: yup.string().required(),
  isGDVSchnittstelle: yup.boolean().required(),
  isVermittlerloginRequired: yup.boolean().required(),
  allowDeckungen: yup.boolean().required(),
  allowVersicherungsbeginn: yup.boolean().required(),
  allowEVBzurUebermittlung: yup.boolean().required(),
  fahrzeugartEingeschraenkt: yup.boolean().required(),
  kennzeichenartEingeschraenkt: yup.boolean().required(),
  maxDauerKurzzeitkennzeichen: yup.number().required(),
  requireGeburtsdatum: yup.boolean().required(),
  requireVertretungsberechtigter: yup.boolean().required(),
  requireHSN: yup.boolean().required(),
  requireTSN: yup.boolean().required(),
  requireSpezielleZugangsdaten: yup.boolean().required(),
  allowAbweichenderHalter: yup.boolean().required(),
  allowAbweichenderHalterJurist: yup.boolean().required(),
  allowVNJurist: yup.boolean().required(),
  fahrzeugArten: yup.array().of(yup.number()).required(),
  kennzeichenArten: yup.array().of(yup.number()).required(),
  kaskoDeckungen: yup.array().of(yup.number()).required(),
});

export type EVBServiceCompany = Asserts<typeof vuInformationSchema>;

export const publicEVBControlServiceResponseSchema = yup.object({
  kennzeichenArten: yup.array().of(keyValueSchema).required(),
  fahrzeugArten: yup.array().of(keyValueSchema).required(),
  kaskoDeckungen: yup.array().of(keyValueSchema).required(),
  gesellschaften: yup.array().of(vuInformationSchema),
});

export type PublicEVBServiceControlResponse = Asserts<
  typeof publicEVBControlServiceResponseSchema
>;

export enum EVB_KENNZEICHEN_TYP {
  KURZZEIT = 1,
  NORMAL = 2,
  SAISON = 3,
}

const anschriftSchema = yup.object({
  strasse: yup.string().required(),
  hausnummer: yup.string().required(),
  postleitzahl: yup.string().required(),
  ort: yup.string().required(),
  land: yup.string().required().oneOf(['D']),
});

export type EVBAddress = yup.Asserts<typeof anschriftSchema>;

const personSchema = yup.object({
  anrede: yup.number().required().oneOf([1, 2]),
  vorname: yup.string().required(),
  nachname: yup.string().required(),
  isVN: yup.boolean().required(),
  geburtsdatum: yup.string().required(),
  anschrift: anschriftSchema,
});

export type EVBPerson = yup.Asserts<typeof personSchema>;

const vertretungsberechtigterSchema = yup.object({
  name: yup.string().required(),
  vorname: yup.string().required(),
});

const juristischePersonSchema = yup.object({
  name: yup.string().required(),
  isVN: yup.boolean().required(),
  requireVertretungsberechtigter: yup.boolean().required(),
  anschrift: anschriftSchema,
  vertretungsberechtigter: vertretungsberechtigterSchema.default(undefined),
});

export type EVBJuristischePerson = yup.Asserts<typeof juristischePersonSchema>;

export enum KENNZEICHEN {
  KURZZEIT = 'RK',
  SAISON = 'ZS',
  NORMAL = 'Z',
}

export const DEFAULT_DAUER_KURZZEIT = 5;

const kennzeichenSchema = yup.object({
  typ: yup
    .string()
    .oneOf([KENNZEICHEN.KURZZEIT, KENNZEICHEN.SAISON, KENNZEICHEN.NORMAL])
    .required(),
  betriebszeitraumVon: yup.number().when('typ', {
    is: (val: string) => val === KENNZEICHEN.SAISON,
    then: yup.number().min(1).max(12).required(),
  }),
  betriebszeitraumBis: yup.number().when('typ', {
    is: (val: string) => val === KENNZEICHEN.SAISON,
    then: yup.number().min(1).max(12).required(),
  }),
  ablauftage: yup.number().when('typ', {
    is: (val: string) => val === KENNZEICHEN.KURZZEIT,
    then: yup.number().required(),
  }),
});

const versicherungsbestaetigungKfzSchema = yup.object({
  versicherungsbeginn: yup.string().default(undefined),
  fahrzeugart: yup.number().required(),
  kennzeichen: kennzeichenSchema,
});

export const eVBDistributionInformationSchema = yup.object({
  wkz: yup.string(),
  usedBrokerId: yup.string().nullable(),
  distributionType: yup.string(),
  poolName: yup.string().nullable(),
});

export type EVBDistributionInformation = Asserts<
  typeof eVBDistributionInformationSchema
>;

export const publicEvbServiceRequestSchema = yup.object({
  vermittlerId: yup.number().required(),
  person: personSchema
    .default(undefined)
    .nullable()
    .when('hasJuristischePerson', {
      is: (val: boolean) => !val,
      then: personSchema.required(),
    }),
  hasJuristischePerson: yup.boolean().required(),
  juristischePerson: juristischePersonSchema
    .default(undefined)
    .nullable()
    .when('hasJuristischePerson', {
      is: (val: boolean) => val,
      then: juristischePersonSchema.required(),
    }),
  versicherungsbestaetigungKfz: versicherungsbestaetigungKfzSchema,
  gesellschaftId: yup.number().required(),
  insuranceId: yup.number().required(),
  gesellschaftName: yup.string().required(),
  deckung: yup.number().nullable().default(undefined),
  deckungsTyp: yup
    .string()
    .oneOf([KASKOTYP.HAFTPFLICHT, KASKOTYP.TEILKASKO, KASKOTYP.VOLLKASKO])
    .nullable()
    .default(null),
  hasJuristischePersonHalter: yup.boolean().default(false),
  abweichenderHalter: yup
    .object()
    .default(undefined)
    .nullable()
    .when('hasJuristischePersonHalter', {
      is: (val: boolean) => val,
      then: personSchema.default(undefined).nullable(),
      otherwise: juristischePersonSchema.default(undefined).nullable(),
    }),
  requiresHsnTsn: yup.boolean().required(),
  hsn: yup.string().when('requiresHsnTsn', {
    is: (val: boolean) => val,
    then: yup.string().required().notOneOf(['']),
  }),
  tsn: yup.string().when('requiresHsnTsn', {
    is: (val: boolean) => val,
    then: yup.string().required().notOneOf(['']),
  }),

  distribution: eVBDistributionInformationSchema,
});

export type PublicEvbServiceRequest = Asserts<
  typeof publicEvbServiceRequestSchema
>;

export interface EVBPrintRequestOptions extends PrintBaseOptions {
  maklerId: number;
  evb: PublicEvbServiceRequest;
  clientDaten: PrintBaseClientData;
  quoteId?: number;
}

export enum VEHICLE_TYPE {
  KRAFTRAD = 1,
  PERSONENKRAFTWAGEN = 2,
  LASTKRAFTWAGEN = 4,
  ZUGMASCHINE = 5,
  ANHAENGER = 7,
  WOHNMOBIL = 8,
}
